<template>
    <iu-input
        class="iu-date-editor"
        :class="'iu-date-editor--' + type"
        :readonly="!editable || readonly || type === 'dates' || type === 'week'"
        :disabled="pickerDisabled"
        :size="pickerSize"
        :name="name"
        v-bind="firstInputId"
        v-if="!ranged"
        v-clickoutside="handleClose"
        :placeholder="placeholder"
        @focus="handleFocus"
        @keydown.native="handleKeydown"
        :value="displayValue"
        @input="value => (userInput = value)"
        @change="handleChange"
        @mouseenter.native="handleMouseEnter"
        @mouseleave.native="showClose = false"
        :validateEvent="false"
        ref="reference"
    >
        <i slot="prefix" class="iu-input__icon" :class="triggerClass" @click="handleFocus"></i>
        <i
            slot="suffix"
            class="iu-input__icon"
            @click="handleClickIcon"
            :class="[showClose ? '' + clearIcon : '']"
            v-if="haveTrigger"
        ></i>
    </iu-input>
    <div
        class="iu-date-editor iu-range-editor iu-input__inner"
        :class="[
            'iu-date-editor--' + type,
            pickerSize ? `iu-range-editor--${pickerSize}` : '',
            pickerDisabled ? 'is-disabled' : '',
            pickerVisible ? 'is-active' : '',
        ]"
        @click="handleRangeClick"
        @mouseenter="handleMouseEnter"
        @mouseleave="showClose = false"
        @keydown="handleKeydown"
        ref="reference"
        v-clickoutside="handleClose"
        v-else
    >
        <i :class="['iu-input__icon', 'iu-range__icon', triggerClass]"></i>
        <input
            autocomplete="off"
            :placeholder="startPlaceholder"
            :value="displayValue && displayValue[0]"
            :disabled="pickerDisabled"
            v-bind="firstInputId"
            :readonly="!editable || readonly"
            :name="name && name[0]"
            @input="handleStartInput"
            @change="handleStartChange"
            @focus="handleFocus"
            class="iu-range-input"
        />
        <slot name="range-separator">
            <span class="iu-range-separator">{{ rangeSeparator }}</span>
        </slot>
        <input
            autocomplete="off"
            :placeholder="endPlaceholder"
            :value="displayValue && displayValue[1]"
            :disabled="pickerDisabled"
            v-bind="secondInputId"
            :readonly="!editable || readonly"
            :name="name && name[1]"
            @input="handleEndInput"
            @change="handleEndChange"
            @focus="handleFocus"
            class="iu-range-input"
        />
        <i
            @click="handleClickIcon"
            v-if="haveTrigger"
            :class="[showClose ? '' + clearIcon : '']"
            class="iu-input__icon iu-range__close-icon"
        ></i>
    </div>
</template>

<script>
import Vue from 'vue';
import Clickoutside from '../../../utils/clickoutside';
import { formatDate, parseDate, isDateObject, getWeekNumber } from '../../../utils/date-util';
import Popper from '../../../utils/vue-popper'; // ?
import Emitter from '../../../utils/emitter';
import IuInput from '../../Input';
import merge from '../../../utils/merge';
const NewPopper = {
    props: {
        appendToBody: Popper.props.appendToBody,
        offset: Popper.props.offset,
        boundariesPadding: Popper.props.boundariesPadding,
        arrowOffset: Popper.props.arrowOffset,
    },
    methods: Popper.methods,
    data() {
        return merge({ visibleArrow: true }, Popper.data);
    },
    beforeDestroy: Popper.beforeDestroy,
};
const DEFAULT_FORMATS = {
    date: 'yyyy-MM-dd',
    month: 'yyyy-MM',
    datetime: 'yyyy-MM-dd HH:mm:ss',
    time: 'HH:mm:ss',
    week: 'yyyywWW',
    timerange: 'HH:mm:ss',
    daterange: 'yyyy-MM-dd',
    monthrange: 'yyyy-MM',
    datetimerange: 'yyyy-MM-dd HH:mm:ss',
    year: 'yyyy',
};
const HAVE_TRIGGER_TYPES = [
    'date',
    'datetime',
    'time',
    'time-select',
    'week',
    'month',
    'year',
    'daterange',
    'monthrange',
    'timerange',
    'datetimerange',
    'dates',
];
const DATE_FORMATTER = function(value, format) {
    if (format === 'timestamp') return value.getTime();
    return formatDate(value, format);
};
const DATE_PARSER = function(text, format) {
    if (format === 'timestamp') return new Date(Number(text));
    return parseDate(text, format);
};
const RANGE_FORMATTER = function(value, format) {
    if (Array.isArray(value) && value.length === 2) {
        const start = value[0];
        const end = value[1];
        if (start && end) {
            return [DATE_FORMATTER(start, format), DATE_FORMATTER(end, format)];
        }
    }
    return '';
};
const RANGE_PARSER = function(array, format, separator) {
    if (!Array.isArray(array)) {
        array = array.split(separator);
    }
    if (array.length === 2) {
        const range1 = array[0];
        const range2 = array[1];
        return [DATE_PARSER(range1, format), DATE_PARSER(range2, format)];
    }
    return [];
};
const TYPE_VALUE_RESOLVER_MAP = {
    default: {
        formatter(value) {
            if (!value) return '';
            return '' + value;
        },
        parser(text) {
            if (text === undefined || text === '') return null;
            return text;
        },
    },
    week: {
        formatter(value, format) {
            let week = getWeekNumber(value);
            let month = value.getMonth();
            const trueDate = new Date(value);
            if (week === 1 && month === 11) {
                trueDate.setHours(0, 0, 0, 0);
                trueDate.setDate(trueDate.getDate() + 3 - ((trueDate.getDay() + 6) % 7));
            }
            let date = formatDate(trueDate, format);
            date = /WW/.test(date) ? date.replace(/WW/, week < 10 ? '0' + week : week) : date.replace(/W/, week);
            return date;
        },
        parser(text, format) {
            // parse as if a normal date
            return TYPE_VALUE_RESOLVER_MAP.date.parser(text, format);
        },
    },
    date: {
        formatter: DATE_FORMATTER,
        parser: DATE_PARSER,
    },
    datetime: {
        formatter: DATE_FORMATTER,
        parser: DATE_PARSER,
    },
    daterange: {
        formatter: RANGE_FORMATTER,
        parser: RANGE_PARSER,
    },
    monthrange: {
        formatter: RANGE_FORMATTER,
        parser: RANGE_PARSER,
    },
    datetimerange: {
        formatter: RANGE_FORMATTER,
        parser: RANGE_PARSER,
    },
    timerange: {
        formatter: RANGE_FORMATTER,
        parser: RANGE_PARSER,
    },
    time: {
        formatter: DATE_FORMATTER,
        parser: DATE_PARSER,
    },
    month: {
        formatter: DATE_FORMATTER,
        parser: DATE_PARSER,
    },
    year: {
        formatter: DATE_FORMATTER,
        parser: DATE_PARSER,
    },
    number: {
        formatter(value) {
            if (!value) return '';
            return '' + value;
        },
        parser(text) {
            let result = Number(text);
            if (!isNaN(text)) {
                return result;
            } else {
                return null;
            }
        },
    },
    dates: {
        formatter(value, format) {
            return value.map(date => DATE_FORMATTER(date, format));
        },
        parser(value, format) {
            return (typeof value === 'string' ? value.split(', ') : value).map(date =>
                date instanceof Date ? date : DATE_PARSER(date, format)
            );
        },
    },
};
const PLACEMENT_MAP = {
    left: 'bottom-start',
    center: 'bottom',
    right: 'bottom-end',
};
const parseAsFormatAndType = (value, customFormat, type, rangeSeparator = '-') => {
    if (!value) return null;
    const parser = (TYPE_VALUE_RESOLVER_MAP[type] || TYPE_VALUE_RESOLVER_MAP['default']).parser;
    const format = customFormat || DEFAULT_FORMATS[type];
    return parser(value, format, rangeSeparator);
};
const formatAsFormatAndType = (value, customFormat, type) => {
    if (!value) return null;
    const formatter = (TYPE_VALUE_RESOLVER_MAP[type] || TYPE_VALUE_RESOLVER_MAP['default']).formatter;
    const format = customFormat || DEFAULT_FORMATS[type];
    return formatter(value, format);
};
/*
 * Considers:
 *   1. Date object
 *   2. date string
 *   3. array of 1 or 2
 */
const valueEquals = function(a, b) {
    // considers Date object and string
    const dateEquals = function(a, b) {
        const aIsDate = a instanceof Date;
        const bIsDate = b instanceof Date;
        if (aIsDate && bIsDate) {
            return a.getTime() === b.getTime();
        }
        if (!aIsDate && !bIsDate) {
            return a === b;
        }
        return false;
    };
    const aIsArray = a instanceof Array;
    const bIsArray = b instanceof Array;
    if (aIsArray && bIsArray) {
        if (a.length !== b.length) {
            return false;
        }
        return a.every((item, index) => dateEquals(item, b[index]));
    }
    if (!aIsArray && !bIsArray) {
        return dateEquals(a, b);
    }
    return false;
};
const isString = function(val) {
    return typeof val === 'string' || val instanceof String;
};
const validator = function(val) {
    // either: String, Array of String, null / undefined
    return (
        val === null ||
        val === undefined ||
        isString(val) ||
        (Array.isArray(val) && val.length === 2 && val.every(isString))
    );
};
export default {
    mixins: [Emitter, NewPopper],
    inject: {
        elForm: {
            default: '',
        },
        elFormItem: {
            default: '',
        },
    },
    props: {
        size: String,
        format: String,
        valueFormat: String,
        readonly: Boolean,
        placeholder: String,
        startPlaceholder: String,
        endPlaceholder: String,
        prefixIcon: String,
        clearIcon: {
            type: String,
            default: 'iu-icon-circle-close',
        },
        name: {
            default: '',
            validator,
        },
        disabled: Boolean,
        clearable: {
            type: Boolean,
            default: true,
        },
        id: {
            default: '',
            validator,
        },
        popperClass: String,
        editable: {
            type: Boolean,
            default: true,
        },
        align: {
            type: String,
            default: 'left',
        },
        value: {},
        defaultValue: {},
        defaultTime: {},
        rangeSeparator: {
            default: '-',
        },
        pickerOptions: {},
        unlinkPanels: Boolean,
        validateEvent: {
            type: Boolean,
            default: true,
        },
    },
    components: { IuInput },
    directives: { Clickoutside },
    data() {
        return {
            pickerVisible: false,
            showClose: false,
            userInput: null,
            valueOnOpen: null, // value when picker opens, used to determine whether to emit change
            unwatchPickerOptions: null,
        };
    },
    watch: {
        pickerVisible(val) {
            if (this.readonly || this.pickerDisabled) return;
            if (val) {
                this.showPicker();
                this.valueOnOpen = Array.isArray(this.value) ? [...this.value] : this.value;
            } else {
                this.hidePicker();
                this.emitChange(this.value);
                this.userInput = null;
                if (this.validateEvent) {
                    this.dispatch('ElFormItem', 'el.form.blur');
                }
                this.$emit('blur', this);
                this.blur();
            }
        },
        parsedValue: {
            immediate: true,
            handler(val) {
                if (this.picker) {
                    this.picker.value = val;
                }
            },
        },
        defaultValue(val) {
            // NOTE: should eventually move to jsx style picker + panel ?
            if (this.picker) {
                this.picker.defaultValue = val;
            }
        },
        value(val, oldVal) {
            if (!valueEquals(val, oldVal) && !this.pickerVisible && this.validateEvent) {
                this.dispatch('ElFormItem', 'el.form.change', val);
            }
        },
    },
    computed: {
        ranged() {
            return this.type.indexOf('range') > -1;
        },
        reference() {
            const reference = this.$refs.reference;
            return reference.$el || reference;
        },
        refInput() {
            if (this.reference) {
                return [].slice.call(this.reference.querySelectorAll('input'));
            }
            return [];
        },
        valueIsEmpty() {
            const val = this.value;
            if (Array.isArray(val)) {
                for (let i = 0, len = val.length; i < len; i++) {
                    if (val[i]) {
                        return false;
                    }
                }
            } else {
                if (val) {
                    return false;
                }
            }
            return true;
        },
        triggerClass() {
            return this.prefixIcon || (this.type.indexOf('time') !== -1 ? 'iu-icon-time' : 'iu-icon-date');
        },
        selectionMode() {
            if (this.type === 'week') {
                return 'week';
            } else if (this.type === 'month') {
                return 'month';
            } else if (this.type === 'year') {
                return 'year';
            } else if (this.type === 'dates') {
                return 'dates';
            }
            return 'day';
        },
        haveTrigger() {
            if (typeof this.showTrigger !== 'undefined') {
                return this.showTrigger;
            }
            return HAVE_TRIGGER_TYPES.indexOf(this.type) !== -1;
        },
        displayValue() {
            const formattedValue = formatAsFormatAndType(this.parsedValue, this.format, this.type, this.rangeSeparator);
            if (Array.isArray(this.userInput)) {
                return [
                    this.userInput[0] || (formattedValue && formattedValue[0]) || '',
                    this.userInput[1] || (formattedValue && formattedValue[1]) || '',
                ];
            } else if (this.userInput !== null) {
                return this.userInput;
            } else if (formattedValue) {
                return this.type === 'dates' ? formattedValue.join(', ') : formattedValue;
            } else {
                return '';
            }
        },
        parsedValue() {
            if (!this.value) return this.value; // component value is not set
            if (this.type === 'time-select') return this.value; // time-select does not require parsing, this might change in next major version
            const valueIsDateObject =
                isDateObject(this.value) || (Array.isArray(this.value) && this.value.every(isDateObject));
            if (valueIsDateObject) {
                return this.value;
            }
            if (this.valueFormat) {
                return parseAsFormatAndType(this.value, this.valueFormat, this.type, this.rangeSeparator) || this.value;
            }
            // NOTE: deal with common but incorrect usage, should remove in next major version
            // user might provide string / timestamp without value-format, coerce them into date (or array of date)
            return Array.isArray(this.value) ? this.value.map(val => new Date(val)) : new Date(this.value);
        },
        _elFormItemSize() {
            return (this.elFormItem || {}).elFormItemSize;
        },
        pickerSize() {
            return this.size || this._elFormItemSize || (this.$ELEMENT || {}).size;
        },
        pickerDisabled() {
            return this.disabled || (this.elForm || {}).disabled;
        },
        firstInputId() {
            const obj = {};
            let id;
            if (this.ranged) {
                id = this.id && this.id[0];
            } else {
                id = this.id;
            }
            if (id) obj.id = id;
            return obj;
        },
        secondInputId() {
            const obj = {};
            let id;
            if (this.ranged) {
                id = this.id && this.id[1];
            }
            if (id) obj.id = id;
            return obj;
        },
    },
    created() {
        // vue-popper
        this.popperOptions = {
            boundariesPadding: 0,
            gpuAcceleration: false,
        };
        this.placement = PLACEMENT_MAP[this.align] || PLACEMENT_MAP.left;
        this.$on('fieldReset', this.handleFieldReset);
    },
    methods: {
        focus() {
            if (!this.ranged) {
                this.$refs.reference.focus();
            } else {
                this.handleFocus();
            }
        },
        blur() {
            this.refInput.forEach(input => input.blur());
        },
        // {parse, formatTo} Value deals maps component value with internal Date
        parseValue(value) {
            const isParsed = isDateObject(value) || (Array.isArray(value) && value.every(isDateObject));
            if (this.valueFormat && !isParsed) {
                return parseAsFormatAndType(value, this.valueFormat, this.type, this.rangeSeparator) || value;
            } else {
                return value;
            }
        },
        formatToValue(date) {
            const isFormattable = isDateObject(date) || (Array.isArray(date) && date.every(isDateObject));
            if (this.valueFormat && isFormattable) {
                return formatAsFormatAndType(date, this.valueFormat, this.type, this.rangeSeparator);
            } else {
                return date;
            }
        },
        // {parse, formatTo} String deals with user input
        parseString(value) {
            const type = Array.isArray(value) ? this.type : this.type.replace('range', '');
            return parseAsFormatAndType(value, this.format, type);
        },
        formatToString(value) {
            const type = Array.isArray(value) ? this.type : this.type.replace('range', '');
            return formatAsFormatAndType(value, this.format, type);
        },
        handleMouseEnter() {
            if (this.readonly || this.pickerDisabled) return;
            if (!this.valueIsEmpty && this.clearable) {
                this.showClose = true;
            }
        },
        handleChange() {
            if (this.userInput) {
                const value = this.parseString(this.displayValue);
                if (value) {
                    this.picker.value = value;
                    if (this.isValidValue(value)) {
                        this.emitInput(value);
                        this.userInput = null;
                    }
                }
            }
            if (this.userInput === '') {
                this.emitInput(null);
                this.emitChange(null);
                this.userInput = null;
            }
        },
        handleStartInput(event) {
            if (this.userInput) {
                this.userInput = [event.target.value, this.userInput[1]];
            } else {
                this.userInput = [event.target.value, null];
            }
        },
        handleEndInput(event) {
            if (this.userInput) {
                this.userInput = [this.userInput[0], event.target.value];
            } else {
                this.userInput = [null, event.target.value];
            }
        },
        handleStartChange() {
            const value = this.parseString(this.userInput && this.userInput[0]);
            if (value) {
                this.userInput = [this.formatToString(value), this.displayValue[1]];
                const newValue = [value, this.picker.value && this.picker.value[1]];
                this.picker.value = newValue;
                if (this.isValidValue(newValue)) {
                    this.emitInput(newValue);
                    this.userInput = null;
                }
            }
        },
        handleEndChange() {
            const value = this.parseString(this.userInput && this.userInput[1]);
            if (value) {
                this.userInput = [this.displayValue[0], this.formatToString(value)];
                const newValue = [this.picker.value && this.picker.value[0], value];
                this.picker.value = newValue;
                if (this.isValidValue(newValue)) {
                    this.emitInput(newValue);
                    this.userInput = null;
                }
            }
        },
        handleClickIcon(event) {
            if (this.readonly || this.pickerDisabled) return;
            if (this.showClose) {
                this.valueOnOpen = this.value;
                event.stopPropagation();
                this.emitInput(null);
                this.emitChange(null);
                this.showClose = false;
                if (this.picker && typeof this.picker.handleClear === 'function') {
                    this.picker.handleClear();
                }
            } else {
                this.pickerVisible = !this.pickerVisible;
            }
        },
        handleClose() {
            if (!this.pickerVisible) return;
            this.pickerVisible = false;
            if (this.type === 'dates') {
                // restore to former value
                const oldValue =
                    parseAsFormatAndType(this.valueOnOpen, this.valueFormat, this.type, this.rangeSeparator) ||
                    this.valueOnOpen;
                this.emitInput(oldValue);
            }
        },
        handleFieldReset(initialValue) {
            this.userInput = initialValue === '' ? null : initialValue;
        },
        handleFocus() {
            const type = this.type;
            if (HAVE_TRIGGER_TYPES.indexOf(type) !== -1 && !this.pickerVisible) {
                this.pickerVisible = true;
            }
            this.$emit('focus', this);
        },
        handleKeydown(event) {
            const keyCode = event.keyCode;
            // ESC
            if (keyCode === 27) {
                this.pickerVisible = false;
                event.stopPropagation();
                return;
            }
            // Tab
            if (keyCode === 9) {
                if (!this.ranged) {
                    this.handleChange();
                    this.pickerVisible = this.picker.visible = false;
                    this.blur();
                    event.stopPropagation();
                } else {
                    // user may change focus between two input
                    setTimeout(() => {
                        if (this.refInput.indexOf(document.activeElement) === -1) {
                            this.pickerVisible = false;
                            this.blur();
                            event.stopPropagation();
                        }
                    }, 0);
                }
                return;
            }
            // Enter
            if (keyCode === 13) {
                if (this.userInput === '' || this.isValidValue(this.parseString(this.displayValue))) {
                    this.handleChange();
                    this.pickerVisible = this.picker.visible = false;
                    this.blur();
                }
                event.stopPropagation();
                return;
            }
            // if user is typing, do not let picker handle key input
            if (this.userInput) {
                event.stopPropagation();
                return;
            }
            // delegate other keys to panel
            if (this.picker && this.picker.handleKeydown) {
                this.picker.handleKeydown(event);
            }
        },
        handleRangeClick() {
            const type = this.type;
            if (HAVE_TRIGGER_TYPES.indexOf(type) !== -1 && !this.pickerVisible) {
                this.pickerVisible = true;
            }
            this.$emit('focus', this);
        },
        hidePicker() {
            if (this.picker) {
                this.picker.resetView && this.picker.resetView();
                this.pickerVisible = this.picker.visible = false;
                this.destroyPopper();
            }
        },
        showPicker() {
            if (this.$isServer) return;
            if (!this.picker) {
                this.mountPicker();
            }
            this.pickerVisible = this.picker.visible = true;
            this.updatePopper();
            this.picker.value = this.parsedValue;
            this.picker.resetView && this.picker.resetView();
            this.$nextTick(() => {
                this.picker.adjustSpinners && this.picker.adjustSpinners();
            });
        },
        mountPicker() {
            this.picker = new Vue(this.panel).$mount();
            this.picker.defaultValue = this.defaultValue;
            this.picker.defaultTime = this.defaultTime;
            this.picker.popperClass = this.popperClass;
            this.popperElm = this.picker.$el;
            this.picker.width = this.reference.getBoundingClientRect().width;
            this.picker.showTime = this.type === 'datetime' || this.type === 'datetimerange';
            this.picker.selectionMode = this.selectionMode;
            this.picker.unlinkPanels = this.unlinkPanels;
            this.picker.arrowControl = this.arrowControl || this.timeArrowControl || false;
            this.$watch('format', format => {
                this.picker.format = format;
            });
            const updateOptions = () => {
                const options = this.pickerOptions;
                if (options && options.selectableRange) {
                    let ranges = options.selectableRange;
                    const parser = TYPE_VALUE_RESOLVER_MAP.datetimerange.parser;
                    const format = DEFAULT_FORMATS.timerange;
                    ranges = Array.isArray(ranges) ? ranges : [ranges];
                    this.picker.selectableRange = ranges.map(range => parser(range, format, this.rangeSeparator));
                }
                for (const option in options) {
                    if (
                        Object.prototype.hasOwnProperty.call(options, option) &&
                        // 忽略 time-picker 的该配置项
                        option !== 'selectableRange'
                    ) {
                        this.picker[option] = options[option];
                    }
                }
                // main format must prevail over undocumented pickerOptions.format
                if (this.format) {
                    this.picker.format = this.format;
                }
            };
            updateOptions();
            this.unwatchPickerOptions = this.$watch('pickerOptions', () => updateOptions(), { deep: true });
            this.$el.appendChild(this.picker.$el);
            this.picker.resetView && this.picker.resetView();
            this.picker.$on('dodestroy', this.doDestroy);
            this.picker.$on('pick', (date = '', visible = false) => {
                this.userInput = null;
                this.pickerVisible = this.picker.visible = visible;
                this.emitInput(date);
                this.picker.resetView && this.picker.resetView();
            });
            this.picker.$on('select-range', (start, end, pos) => {
                if (this.refInput.length === 0) return;
                if (!pos || pos === 'min') {
                    this.refInput[0].setSelectionRange(start, end);
                    this.refInput[0].focus();
                } else if (pos === 'max') {
                    this.refInput[1].setSelectionRange(start, end);
                    this.refInput[1].focus();
                }
            });
        },
        unmountPicker() {
            if (this.picker) {
                this.picker.$destroy();
                this.picker.$off();
                if (typeof this.unwatchPickerOptions === 'function') {
                    this.unwatchPickerOptions();
                }
                this.picker.$el.parentNode.removeChild(this.picker.$el);
            }
        },
        emitChange(val) {
            // determine user real change only
            if (!valueEquals(val, this.valueOnOpen)) {
                this.$emit('change', val);
                this.valueOnOpen = val;
                if (this.validateEvent) {
                    this.dispatch('ElFormItem', 'el.form.change', val);
                }
            }
        },
        emitInput(val) {
            const formatted = this.formatToValue(val);
            if (!valueEquals(this.value, formatted)) {
                this.$emit('input', formatted);
            }
        },
        isValidValue(value) {
            if (!this.picker) {
                this.mountPicker();
            }
            if (this.picker.isValidValue) {
                return value && this.picker.isValidValue(value);
            } else {
                return true;
            }
        },
    },
};
</script>

<style scope>
.iu-date-table td.in-range div,
.iu-date-table td.in-range div:hover,
.iu-date-table.is-week-mode .iu-date-table__row.current div,
.iu-date-table.is-week-mode .iu-date-table__row:hover div {
    background-color: #f2f6fc;
}
.iu-date-table {
    font-size: 12px;
    -webkit-user-select: none;
    -moz-user-select: none;
    -ms-user-select: none;
    user-select: none;
}
.iu-date-table.is-week-mode .iu-date-table__row:hover td.available:hover {
    color: #606266;
}
.iu-date-table.is-week-mode .iu-date-table__row:hover td:first-child div {
    margin-left: 5px;
    border-top-left-radius: 15px;
    border-bottom-left-radius: 15px;
}
.iu-date-table.is-week-mode .iu-date-table__row:hover td:last-child div {
    margin-right: 5px;
    border-top-right-radius: 15px;
    border-bottom-right-radius: 15px;
}
.iu-date-table td {
    width: 32px;
    height: 30px;
    padding: 4px 0;
    -webkit-box-sizing: border-box;
    box-sizing: border-box;
    text-align: center;
    cursor: pointer;
    position: relative;
}
.iu-date-table td div {
    height: 30px;
    padding: 3px 0;
    -webkit-box-sizing: border-box;
    box-sizing: border-box;
}
.iu-date-table td span {
    width: 24px;
    height: 24px;
    display: block;
    margin: 0 auto;
    line-height: 24px;
    position: absolute;
    left: 50%;
    -webkit-transform: translateX(-50%);
    transform: translateX(-50%);
    border-radius: 50%;
}
.iu-date-table td.next-month,
.iu-date-table td.prev-month {
    color: #c0c4cc;
}
.iu-date-table td.today {
    position: relative;
}
.iu-date-table td.today span {
    color: #409eff;
    font-weight: 700;
}
.iu-date-table td.today.end-date span,
.iu-date-table td.today.start-date span {
    color: #fff;
}
.iu-date-table td.available:hover {
    color: #409eff;
}
.iu-date-table td.current:not(.disabled) span {
    color: #fff;
    background-color: #409eff;
}
.iu-date-table td.end-date div,
.iu-date-table td.start-date div {
    color: #fff;
}
.iu-date-table td.end-date span,
.iu-date-table td.start-date span {
    background-color: #409eff;
}
.iu-date-table td.start-date div {
    margin-left: 5px;
    border-top-left-radius: 15px;
    border-bottom-left-radius: 15px;
}
.iu-date-table td.end-date div {
    margin-right: 5px;
    border-top-right-radius: 15px;
    border-bottom-right-radius: 15px;
}
.iu-date-table td.disabled div {
    background-color: #f5f7fa;
    opacity: 1;
    cursor: not-allowed;
    color: #c0c4cc;
}
.iu-fade-in-enter,
.iu-fade-in-leave-active,
.iu-fade-in-linear-enter,
.iu-fade-in-linear-leave,
.iu-fade-in-linear-leave-active,
.fade-in-linear-enter,
.fade-in-linear-leave,
.fade-in-linear-leave-active {
    opacity: 0;
}
.iu-date-table td.selected div {
    margin-left: 5px;
    margin-right: 5px;
    background-color: #f2f6fc;
    border-radius: 15px;
}
.iu-date-table td.selected div:hover {
    background-color: #f2f6fc;
}
.iu-date-table td.selected span {
    background-color: #409eff;
    color: #fff;
    border-radius: 15px;
}
.iu-date-table td.week {
    font-size: 80%;
    color: #606266;
}
.iu-date-table th {
    padding: 5px;
    color: #606266;
    font-weight: 400;
    border-bottom: solid 1px #ebeef5;
}
.iu-month-table {
    font-size: 12px;
    margin: -1px;
    border-collapse: collapse;
}
.iu-month-table td {
    text-align: center;
    padding: 8px 0;
    cursor: pointer;
}
.iu-month-table td div {
    height: 48px;
    padding: 6px 0;
    -webkit-box-sizing: border-box;
    box-sizing: border-box;
}
.iu-month-table td.today .cell {
    color: #409eff;
    font-weight: 700;
}
.iu-month-table td.today.end-date .cell,
.iu-month-table td.today.start-date .cell {
    color: #fff;
}
.iu-month-table td.disabled .cell {
    background-color: #f5f7fa;
    cursor: not-allowed;
    color: #c0c4cc;
}
.iu-month-table td.disabled .cell:hover {
    color: #c0c4cc;
}
.iu-month-table td .cell {
    width: 60px;
    height: 36px;
    display: block;
    line-height: 36px;
    color: #606266;
    margin: 0 auto;
    border-radius: 18px;
}
.iu-month-table td .cell:hover {
    color: #409eff;
}
.iu-month-table td.in-range div,
.iu-month-table td.in-range div:hover {
    background-color: #f2f6fc;
}
.iu-month-table td.end-date div,
.iu-month-table td.start-date div {
    color: #fff;
}
.iu-month-table td.end-date .cell,
.iu-month-table td.start-date .cell {
    color: #fff;
    background-color: #409eff;
}
.iu-month-table td.start-date div {
    border-top-left-radius: 24px;
    border-bottom-left-radius: 24px;
}
.iu-month-table td.end-date div {
    border-top-right-radius: 24px;
    border-bottom-right-radius: 24px;
}
.iu-month-table td.current:not(.disabled) .cell {
    color: #409eff;
}
.iu-year-table {
    font-size: 12px;
    margin: -1px;
    border-collapse: collapse;
}
.iu-year-table .iu-icon {
    color: #303133;
}
.iu-year-table td {
    text-align: center;
    padding: 20px 3px;
    cursor: pointer;
}
.iu-year-table td.today .cell {
    color: #409eff;
    font-weight: 700;
}
.iu-year-table td.disabled .cell {
    background-color: #f5f7fa;
    cursor: not-allowed;
    color: #c0c4cc;
}
.iu-year-table td.disabled .cell:hover {
    color: #c0c4cc;
}
.iu-year-table td .cell {
    width: 48px;
    height: 32px;
    display: block;
    line-height: 32px;
    color: #606266;
    margin: 0 auto;
}
.iu-year-table td .cell:hover,
.iu-year-table td.current:not(.disabled) .cell {
    color: #409eff;
}
.iu-time-spinner.has-seconds .iu-time-spinner__wrapper {
    width: 33.3%;
}
.iu-time-spinner__wrapper {
    max-height: 190px;
    overflow: auto;
    display: inline-block;
    width: 50%;
    vertical-align: top;
    position: relative;
}
.iu-time-spinner__wrapper .iu-scrollbar__wrap:not(.iu-scrollbar__wrap--hidden-default) {
    padding-bottom: 15px;
}
.iu-time-spinner__wrapper.is-arrow {
    -webkit-box-sizing: border-box;
    box-sizing: border-box;
    text-align: center;
    overflow: hidden;
}
.iu-time-spinner__wrapper.is-arrow .iu-time-spinner__list {
    -webkit-transform: translateY(-32px);
    transform: translateY(-32px);
}
.iu-time-spinner__wrapper.is-arrow .iu-time-spinner__item:hover:not(.disabled):not(.active) {
    background: #fff;
    cursor: default;
}
.iu-time-spinner__arrow {
    font-size: 12px;
    color: #909399;
    position: absolute;
    left: 0;
    width: 100%;
    z-index: 1;
    text-align: center;
    height: 30px;
    line-height: 30px;
    cursor: pointer;
}
.iu-time-spinner__arrow:hover {
    color: #409eff;
}
.iu-time-spinner__arrow.iu-icon-arrow-up {
    top: 10px;
}
.iu-time-spinner__arrow.iu-icon-arrow-down {
    bottom: 10px;
}
.iu-time-spinner__input.iu-input {
    width: 70%;
}
.iu-time-spinner__input.iu-input .iu-input__inner {
    padding: 0;
    text-align: center;
}
.iu-time-spinner__list {
    padding: 0;
    margin: 0;
    list-style: none;
    text-align: center;
}
.iu-time-spinner__list::after,
.iu-time-spinner__list::before {
    content: '';
    display: block;
    width: 100%;
    height: 80px;
}
.iu-time-spinner__item {
    height: 32px;
    line-height: 32px;
    font-size: 12px;
    color: #606266;
}
.iu-time-spinner__item:hover:not(.disabled):not(.active) {
    background: #f5f7fa;
    cursor: pointer;
}
.iu-time-spinner__item.active:not(.disabled) {
    color: #303133;
    font-weight: 700;
}
.iu-time-spinner__item.disabled {
    color: #c0c4cc;
    cursor: not-allowed;
}
.fade-in-linear-enter-active,
.fade-in-linear-leave-active {
    -webkit-transition: opacity 0.2s linear;
    transition: opacity 0.2s linear;
}
.iu-fade-in-linear-enter-active,
.iu-fade-in-linear-leave-active {
    -webkit-transition: opacity 0.2s linear;
    transition: opacity 0.2s linear;
}
.iu-fade-in-enter-active,
.iu-fade-in-leave-active {
    -webkit-transition: all 0.3s cubic-bezier(0.55, 0, 0.1, 1);
    transition: all 0.3s cubic-bezier(0.55, 0, 0.1, 1);
}
.iu-zoom-in-center-enter-active,
.iu-zoom-in-center-leave-active {
    -webkit-transition: all 0.3s cubic-bezier(0.55, 0, 0.1, 1);
    transition: all 0.3s cubic-bezier(0.55, 0, 0.1, 1);
}
.iu-zoom-in-center-enter,
.iu-zoom-in-center-leave-active {
    opacity: 0;
    -webkit-transform: scaleX(0);
    transform: scaleX(0);
}
.iu-zoom-in-top-enter-active,
.iu-zoom-in-top-leave-active {
    opacity: 1;
    -webkit-transform: scaleY(1);
    transform: scaleY(1);
    -webkit-transition: opacity 0.3s cubic-bezier(0.23, 1, 0.32, 1),
        -webkit-transform 0.3s cubic-bezier(0.23, 1, 0.32, 1);
    transition: opacity 0.3s cubic-bezier(0.23, 1, 0.32, 1), -webkit-transform 0.3s cubic-bezier(0.23, 1, 0.32, 1);
    transition: transform 0.3s cubic-bezier(0.23, 1, 0.32, 1), opacity 0.3s cubic-bezier(0.23, 1, 0.32, 1);
    transition: transform 0.3s cubic-bezier(0.23, 1, 0.32, 1), opacity 0.3s cubic-bezier(0.23, 1, 0.32, 1),
        -webkit-transform 0.3s cubic-bezier(0.23, 1, 0.32, 1);
    -webkit-transform-origin: center top;
    transform-origin: center top;
}
.iu-zoom-in-top-enter,
.iu-zoom-in-top-leave-active {
    opacity: 0;
    -webkit-transform: scaleY(0);
    transform: scaleY(0);
}
.iu-zoom-in-bottom-enter-active,
.iu-zoom-in-bottom-leave-active {
    opacity: 1;
    -webkit-transform: scaleY(1);
    transform: scaleY(1);
    -webkit-transition: opacity 0.3s cubic-bezier(0.23, 1, 0.32, 1),
        -webkit-transform 0.3s cubic-bezier(0.23, 1, 0.32, 1);
    transition: opacity 0.3s cubic-bezier(0.23, 1, 0.32, 1), -webkit-transform 0.3s cubic-bezier(0.23, 1, 0.32, 1);
    transition: transform 0.3s cubic-bezier(0.23, 1, 0.32, 1), opacity 0.3s cubic-bezier(0.23, 1, 0.32, 1);
    transition: transform 0.3s cubic-bezier(0.23, 1, 0.32, 1), opacity 0.3s cubic-bezier(0.23, 1, 0.32, 1),
        -webkit-transform 0.3s cubic-bezier(0.23, 1, 0.32, 1);
    -webkit-transform-origin: center bottom;
    transform-origin: center bottom;
}
.iu-zoom-in-bottom-enter,
.iu-zoom-in-bottom-leave-active {
    opacity: 0;
    -webkit-transform: scaleY(0);
    transform: scaleY(0);
}
.iu-zoom-in-left-enter-active,
.iu-zoom-in-left-leave-active {
    opacity: 1;
    -webkit-transform: scale(1, 1);
    transform: scale(1, 1);
    -webkit-transition: opacity 0.3s cubic-bezier(0.23, 1, 0.32, 1),
        -webkit-transform 0.3s cubic-bezier(0.23, 1, 0.32, 1);
    transition: opacity 0.3s cubic-bezier(0.23, 1, 0.32, 1), -webkit-transform 0.3s cubic-bezier(0.23, 1, 0.32, 1);
    transition: transform 0.3s cubic-bezier(0.23, 1, 0.32, 1), opacity 0.3s cubic-bezier(0.23, 1, 0.32, 1);
    transition: transform 0.3s cubic-bezier(0.23, 1, 0.32, 1), opacity 0.3s cubic-bezier(0.23, 1, 0.32, 1),
        -webkit-transform 0.3s cubic-bezier(0.23, 1, 0.32, 1);
    -webkit-transform-origin: top left;
    transform-origin: top left;
}
.iu-zoom-in-left-enter,
.iu-zoom-in-left-leave-active {
    opacity: 0;
    -webkit-transform: scale(0.45, 0.45);
    transform: scale(0.45, 0.45);
}
.collapse-transition {
    -webkit-transition: 0.3s height ease-in-out, 0.3s padding-top ease-in-out, 0.3s padding-bottom ease-in-out;
    transition: 0.3s height ease-in-out, 0.3s padding-top ease-in-out, 0.3s padding-bottom ease-in-out;
}
.horizontal-collapse-transition {
    -webkit-transition: 0.3s width ease-in-out, 0.3s padding-left ease-in-out, 0.3s padding-right ease-in-out;
    transition: 0.3s width ease-in-out, 0.3s padding-left ease-in-out, 0.3s padding-right ease-in-out;
}
.iu-list-enter-active,
.iu-list-leave-active {
    -webkit-transition: all 1s;
    transition: all 1s;
}
.iu-list-enter,
.iu-list-leave-active {
    opacity: 0;
    -webkit-transform: translateY(-30px);
    transform: translateY(-30px);
}
.iu-opacity-transition {
    -webkit-transition: opacity 0.3s cubic-bezier(0.55, 0, 0.1, 1);
    transition: opacity 0.3s cubic-bezier(0.55, 0, 0.1, 1);
}
.iu-date-editor {
    position: relative;
    display: inline-block;
    text-align: left;
}
.iu-date-editor.iu-input,
.iu-date-editor.iu-input__inner {
    width: 220px;
}
.iu-date-editor--monthrange.iu-input,
.iu-date-editor--monthrange.iu-input__inner {
    width: 300px;
}
.iu-date-editor--daterange.iu-input,
.iu-date-editor--daterange.iu-input__inner,
.iu-date-editor--timerange.iu-input,
.iu-date-editor--timerange.iu-input__inner {
    width: 350px;
}
.iu-date-editor--datetimerange.iu-input,
.iu-date-editor--datetimerange.iu-input__inner {
    width: 400px;
}
.iu-date-editor--dates .iu-input__inner {
    text-overflow: ellipsis;
    white-space: nowrap;
}
.iu-date-editor .iu-icon-circle-close {
    cursor: pointer;
}
.iu-date-editor .iu-range__icon {
    font-size: 14px;
    margin-left: -5px;
    color: #c0c4cc;
    float: left;
    line-height: 32px;
}
.iu-date-editor .iu-range-input,
.iu-date-editor .iu-range-separator {
    height: 100%;
    margin: 0;
    text-align: center;
    display: inline-block;
    font-size: 14px;
}
.iu-date-editor .iu-range-input {
    -webkit-appearance: none;
    -moz-appearance: none;
    appearance: none;
    border: none;
    outline: 0;
    padding: 0;
    width: 39%;
    color: #606266;
}
.iu-date-editor .iu-range-input::-webkit-input-placeholder {
    color: #c0c4cc;
}
.iu-date-editor .iu-range-input:-ms-input-placeholder {
    color: #c0c4cc;
}
.iu-date-editor .iu-range-input::-ms-input-placeholder {
    color: #c0c4cc;
}
.iu-date-editor .iu-range-input::placeholder {
    color: #c0c4cc;
}
.iu-date-editor .iu-range-separator {
    padding: 0 5px;
    line-height: 32px;
    width: 5%;
    color: #303133;
}
.iu-date-editor .iu-range__close-icon {
    font-size: 14px;
    color: #c0c4cc;
    width: 25px;
    display: inline-block;
    float: right;
    line-height: 32px;
}
.iu-range-editor.iu-input__inner {
    display: -webkit-inline-box;
    display: -ms-inline-flexbox;
    display: inline-flex;
    -webkit-box-align: center;
    -ms-flex-align: center;
    align-items: center;
    padding: 3px 10px;
}
.iu-range-editor .iu-range-input {
    line-height: 1;
}
.iu-range-editor.is-active,
.iu-range-editor.is-active:hover {
    border-color: #409eff;
}
.iu-range-editor--medium.iu-input__inner {
    height: 36px;
}
.iu-range-editor--medium .iu-range-separator {
    line-height: 28px;
    font-size: 14px;
}
.iu-range-editor--medium .iu-range-input {
    font-size: 14px;
}
.iu-range-editor--medium .iu-range__close-icon,
.iu-range-editor--medium .iu-range__icon {
    line-height: 28px;
}
.iu-range-editor--small.iu-input__inner {
    height: 32px;
}
.iu-range-editor--small .iu-range-separator {
    line-height: 24px;
    font-size: 13px;
}
.iu-range-editor--small .iu-range-input {
    font-size: 13px;
}
.iu-range-editor--small .iu-range__close-icon,
.iu-range-editor--small .iu-range__icon {
    line-height: 24px;
}
.iu-range-editor--mini.iu-input__inner {
    height: 28px;
}
.iu-range-editor--mini .iu-range-separator {
    line-height: 20px;
    font-size: 12px;
}
.iu-range-editor--mini .iu-range-input {
    font-size: 12px;
}
.iu-range-editor--mini .iu-range__close-icon,
.iu-range-editor--mini .iu-range__icon {
    line-height: 20px;
}
.iu-range-editor.is-disabled {
    background-color: #f5f7fa;
    border-color: #e4e7ed;
    color: #c0c4cc;
    cursor: not-allowed;
}
.iu-range-editor.is-disabled:focus,
.iu-range-editor.is-disabled:hover {
    border-color: #e4e7ed;
}
.iu-range-editor.is-disabled input {
    background-color: #f5f7fa;
    color: #c0c4cc;
    cursor: not-allowed;
}
.iu-range-editor.is-disabled input::-webkit-input-placeholder {
    color: #c0c4cc;
}
.iu-range-editor.is-disabled input:-ms-input-placeholder {
    color: #c0c4cc;
}
.iu-range-editor.is-disabled input::-ms-input-placeholder {
    color: #c0c4cc;
}
.iu-range-editor.is-disabled input::placeholder {
    color: #c0c4cc;
}
.iu-range-editor.is-disabled .iu-range-separator {
    color: #c0c4cc;
}
.iu-picker-panel {
    color: #606266;
    border: 1px solid #e4e7ed;
    -webkit-box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
    box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
    background: #fff;
    border-radius: 4px;
    line-height: 30px;
    margin: 5px 0;
}
.iu-picker-panel__body-wrapper::after,
.iu-picker-panel__body::after {
    content: '';
    display: table;
    clear: both;
}
.iu-picker-panel__content {
    position: relative;
    margin: 15px;
}
.iu-picker-panel__footer {
    border-top: 1px solid #e4e4e4;
    padding: 4px;
    text-align: right;
    background-color: #fff;
    position: relative;
    font-size: 0;
}
.iu-picker-panel__shortcut {
    display: block;
    width: 100%;
    border: 0;
    background-color: transparent;
    line-height: 28px;
    font-size: 14px;
    color: #606266;
    padding-left: 12px;
    text-align: left;
    outline: 0;
    cursor: pointer;
}
.iu-picker-panel__shortcut:hover {
    color: #409eff;
}
.iu-picker-panel__shortcut.active {
    background-color: #e6f1fe;
    color: #409eff;
}
.iu-picker-panel__btn {
    border: 1px solid #dcdcdc;
    color: #333;
    line-height: 24px;
    border-radius: 2px;
    padding: 0 20px;
    cursor: pointer;
    background-color: transparent;
    outline: 0;
    font-size: 12px;
}
.iu-picker-panel__btn[disabled] {
    color: #ccc;
    cursor: not-allowed;
}
.iu-picker-panel__icon-btn {
    font-size: 12px;
    color: #303133;
    border: 0;
    background: 0 0;
    cursor: pointer;
    outline: 0;
    margin-top: 8px;
}
.iu-picker-panel__icon-btn:hover {
    color: #409eff;
}
.iu-picker-panel__icon-btn.is-disabled {
    color: #bbb;
}
.iu-picker-panel__icon-btn.is-disabled:hover {
    cursor: not-allowed;
}
.iu-picker-panel__link-btn {
    vertical-align: middle;
}
.iu-picker-panel [slot='sidebar'],
.iu-picker-panel__sidebar {
    position: absolute;
    top: 0;
    bottom: 0;
    width: 110px;
    border-right: 1px solid #e4e4e4;
    -webkit-box-sizing: border-box;
    box-sizing: border-box;
    padding-top: 6px;
    background-color: #fff;
    overflow: auto;
}
.iu-picker-panel [slot='sidebar'] + .iu-picker-panel__body,
.iu-picker-panel__sidebar + .iu-picker-panel__body {
    margin-left: 110px;
}
.iu-date-picker {
    width: 322px;
}
.iu-date-picker.has-sidebar.has-time {
    width: 434px;
}
.iu-date-picker.has-sidebar {
    width: 438px;
}
.iu-date-picker.has-time .iu-picker-panel__body-wrapper {
    position: relative;
}
.iu-date-picker .iu-picker-panel__content {
    width: 292px;
}
.iu-date-picker table {
    table-layout: fixed;
    width: 100%;
}
.iu-date-picker__editor-wrap {
    position: relative;
    display: table-cell;
    padding: 0 5px;
}
.iu-date-picker__time-header {
    position: relative;
    border-bottom: 1px solid #e4e4e4;
    font-size: 12px;
    padding: 8px 5px 5px;
    display: table;
    width: 100%;
    -webkit-box-sizing: border-box;
    box-sizing: border-box;
}
.iu-date-picker__header {
    margin: 12px;
    text-align: center;
}
.iu-date-picker__header--bordered {
    margin-bottom: 0;
    padding-bottom: 12px;
    border-bottom: solid 1px #ebeef5;
}
.iu-date-picker__header--bordered + .iu-picker-panel__content {
    margin-top: 0;
}
.iu-date-picker__header-label {
    font-size: 16px;
    font-weight: 500;
    padding: 0 5px;
    line-height: 22px;
    text-align: center;
    cursor: pointer;
    color: #606266;
}
.iu-date-picker__header-label.active,
.iu-date-picker__header-label:hover {
    color: #409eff;
}
.iu-date-picker__prev-btn {
    float: left;
}
.iu-date-picker__next-btn {
    float: right;
}
.iu-date-picker__time-wrap {
    padding: 10px;
    text-align: center;
}
.iu-date-picker__time-label {
    float: left;
    cursor: pointer;
    line-height: 30px;
    margin-left: 10px;
}
.iu-date-range-picker {
    width: 646px;
}
.iu-date-range-picker.has-sidebar {
    width: 756px;
}
.iu-date-range-picker table {
    table-layout: fixed;
    width: 100%;
}
.iu-date-range-picker .iu-picker-panel__body {
    min-width: 513px;
}
.iu-date-range-picker .iu-picker-panel__content {
    margin: 0;
}
.iu-date-range-picker__header {
    position: relative;
    text-align: center;
    height: 28px;
}
.iu-date-range-picker__header [class*='arrow-left'] {
    float: left;
}
.iu-date-range-picker__header [class*='arrow-right'] {
    float: right;
}
.iu-date-range-picker__header div {
    font-size: 16px;
    font-weight: 500;
    margin-right: 50px;
}
.iu-date-range-picker__content {
    float: left;
    width: 50%;
    -webkit-box-sizing: border-box;
    box-sizing: border-box;
    margin: 0;
    padding: 16px;
}
.iu-date-range-picker__content.is-left {
    border-right: 1px solid #e4e4e4;
}
.iu-date-range-picker__content .iu-date-range-picker__header div {
    margin-left: 50px;
    margin-right: 50px;
}
.iu-date-range-picker__editors-wrap {
    -webkit-box-sizing: border-box;
    box-sizing: border-box;
    display: table-cell;
}
.iu-date-range-picker__editors-wrap.is-right {
    text-align: right;
}
.iu-date-range-picker__time-header {
    position: relative;
    border-bottom: 1px solid #e4e4e4;
    font-size: 12px;
    padding: 8px 5px 5px;
    display: table;
    width: 100%;
    -webkit-box-sizing: border-box;
    box-sizing: border-box;
}
.iu-date-range-picker__time-header > .iu-icon-arrow-right {
    font-size: 20px;
    vertical-align: middle;
    display: table-cell;
    color: #303133;
}
.iu-date-range-picker__time-picker-wrap {
    position: relative;
    display: table-cell;
    padding: 0 5px;
}
.iu-date-range-picker__time-picker-wrap .iu-picker-panel {
    position: absolute;
    top: 13px;
    right: 0;
    z-index: 1;
    background: #fff;
}
.iu-time-range-picker {
    width: 354px;
    overflow: visible;
}
.iu-time-range-picker__content {
    position: relative;
    text-align: center;
    padding: 10px;
}
.iu-time-range-picker__cell {
    -webkit-box-sizing: border-box;
    box-sizing: border-box;
    margin: 0;
    padding: 4px 7px 7px;
    width: 50%;
    display: inline-block;
}
.iu-time-range-picker__header {
    margin-bottom: 5px;
    text-align: center;
    font-size: 14px;
}
.iu-time-range-picker__body {
    border-radius: 2px;
    border: 1px solid #e4e7ed;
}
.iu-time-panel {
    margin: 5px 0;
    border: 1px solid #e4e7ed;
    background-color: #fff;
    -webkit-box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
    box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
    border-radius: 2px;
    position: absolute;
    width: 180px;
    left: 0;
    z-index: 1000;
    -webkit-user-select: none;
    -moz-user-select: none;
    -ms-user-select: none;
    user-select: none;
    -webkit-box-sizing: content-box;
    box-sizing: content-box;
}
.iu-time-panel__content {
    font-size: 0;
    position: relative;
    overflow: hidden;
}
.iu-time-panel__content::after,
.iu-time-panel__content::before {
    content: '';
    top: 50%;
    position: absolute;
    margin-top: -15px;
    height: 32px;
    z-index: -1;
    left: 0;
    right: 0;
    -webkit-box-sizing: border-box;
    box-sizing: border-box;
    padding-top: 6px;
    text-align: left;
    border-top: 1px solid #e4e7ed;
    border-bottom: 1px solid #e4e7ed;
}
.iu-time-panel__content::after {
    left: 50%;
    margin-left: 12%;
    margin-right: 12%;
}
.iu-time-panel__content::before {
    padding-left: 50%;
    margin-right: 12%;
    margin-left: 12%;
}
.iu-time-panel__content.has-seconds::after {
    left: calc(100% / 3 * 2);
}
.iu-time-panel__content.has-seconds::before {
    padding-left: calc(100% / 3);
}
.iu-time-panel__footer {
    border-top: 1px solid #e4e4e4;
    padding: 4px;
    height: 36px;
    line-height: 25px;
    text-align: right;
    -webkit-box-sizing: border-box;
    box-sizing: border-box;
}
.iu-time-panel__btn {
    border: none;
    line-height: 28px;
    padding: 0 5px;
    margin: 0 5px;
    cursor: pointer;
    background-color: transparent;
    outline: 0;
    font-size: 12px;
    color: #303133;
}
.iu-time-panel__btn.confirm {
    font-weight: 800;
    color: #409eff;
}
.iu-textarea {
    position: relative;
    display: inline-block;
    width: 100%;
    vertical-align: bottom;
    font-size: 14px;
}
.iu-textarea__inner {
    display: block;
    resize: vertical;
    padding: 5px 15px;
    line-height: 1.5;
    -webkit-box-sizing: border-box;
    box-sizing: border-box;
    width: 100%;
    font-size: inherit;
    color: #606266;
    background-color: #fff;
    background-image: none;
    border: 1px solid #dcdfe6;
    border-radius: 4px;
    -webkit-transition: border-color 0.2s cubic-bezier(0.645, 0.045, 0.355, 1);
    transition: border-color 0.2s cubic-bezier(0.645, 0.045, 0.355, 1);
}
.iu-textarea__inner::-webkit-input-placeholder {
    color: #c0c4cc;
}
.iu-textarea__inner:-ms-input-placeholder {
    color: #c0c4cc;
}
.iu-textarea__inner::-ms-input-placeholder {
    color: #c0c4cc;
}
.iu-textarea__inner::placeholder {
    color: #c0c4cc;
}
.iu-textarea__inner:hover {
    border-color: #c0c4cc;
}
.iu-textarea__inner:focus {
    outline: 0;
    border-color: #409eff;
}
.iu-textarea .iu-input__count {
    color: #909399;
    background: #fff;
    position: absolute;
    font-size: 12px;
    bottom: 5px;
    right: 10px;
}
.iu-textarea.is-disabled .iu-textarea__inner {
    background-color: #f5f7fa;
    border-color: #e4e7ed;
    color: #c0c4cc;
    cursor: not-allowed;
}
.iu-textarea.is-disabled .iu-textarea__inner::-webkit-input-placeholder {
    color: #c0c4cc;
}
.iu-textarea.is-disabled .iu-textarea__inner:-ms-input-placeholder {
    color: #c0c4cc;
}
.iu-textarea.is-disabled .iu-textarea__inner::-ms-input-placeholder {
    color: #c0c4cc;
}
.iu-textarea.is-disabled .iu-textarea__inner::placeholder {
    color: #c0c4cc;
}
.iu-textarea.is-exceed .iu-textarea__inner {
    border-color: #f56c6c;
}
.iu-textarea.is-exceed .iu-input__count {
    color: #f56c6c;
}
.iu-input {
    position: relative;
    font-size: 14px;
    display: inline-block;
    width: 100%;
}
.iu-input::-webkit-scrollbar {
    z-index: 11;
    width: 6px;
}
.iu-input::-webkit-scrollbar:horizontal {
    height: 6px;
}
.iu-input::-webkit-scrollbar-thumb {
    border-radius: 5px;
    width: 6px;
    background: #b4bccc;
}
.iu-input::-webkit-scrollbar-corner {
    background: #fff;
}
.iu-input::-webkit-scrollbar-track {
    background: #fff;
}
.iu-input::-webkit-scrollbar-track-piece {
    background: #fff;
    width: 6px;
}
.iu-input .iu-input__clear {
    color: #c0c4cc;
    font-size: 14px;
    cursor: pointer;
    -webkit-transition: color 0.2s cubic-bezier(0.645, 0.045, 0.355, 1);
    transition: color 0.2s cubic-bezier(0.645, 0.045, 0.355, 1);
}
.iu-input .iu-input__clear:hover {
    color: #909399;
}
.iu-input .iu-input__count {
    height: 100%;
    display: -webkit-inline-box;
    display: -ms-inline-flexbox;
    display: inline-flex;
    -webkit-box-align: center;
    -ms-flex-align: center;
    align-items: center;
    color: #909399;
    font-size: 12px;
}
.iu-input .iu-input__count .iu-input__count-inner {
    background: #fff;
    line-height: initial;
    display: inline-block;
    padding: 0 5px;
}
.iu-input__inner {
    -webkit-appearance: none;
    background-color: #fff;
    background-image: none;
    border-radius: 4px;
    border: 1px solid #dcdfe6;
    -webkit-box-sizing: border-box;
    box-sizing: border-box;
    color: #606266;
    display: inline-block;
    font-size: inherit;
    height: 40px;
    line-height: 40px;
    outline: 0;
    padding: 0 15px;
    -webkit-transition: border-color 0.2s cubic-bezier(0.645, 0.045, 0.355, 1);
    transition: border-color 0.2s cubic-bezier(0.645, 0.045, 0.355, 1);
    width: 100%;
}
.iu-input__prefix,
.iu-input__suffix {
    position: absolute;
    top: 0;
    -webkit-transition: all 0.3s;
    text-align: center;
    height: 100%;
    color: #c0c4cc;
}
.iu-input__inner::-webkit-input-placeholder {
    color: #c0c4cc;
}
.iu-input__inner:-ms-input-placeholder {
    color: #c0c4cc;
}
.iu-input__inner::-ms-input-placeholder {
    color: #c0c4cc;
}
.iu-input__inner::placeholder {
    color: #c0c4cc;
}
.iu-input__inner:hover {
    border-color: #c0c4cc;
}
.iu-input.is-active .iu-input__inner,
.iu-input__inner:focus {
    border-color: #409eff;
    outline: 0;
}
.iu-input__suffix {
    right: 5px;
    transition: all 0.3s;
    pointer-events: none;
}
.iu-input__suffix-inner {
    pointer-events: all;
}
.iu-input__prefix {
    left: 5px;
    transition: all 0.3s;
}
.iu-input__icon {
    height: 100%;
    width: 25px;
    text-align: center;
    -webkit-transition: all 0.3s;
    transition: all 0.3s;
    line-height: 40px;
}
.iu-input__icon:after {
    content: '';
    height: 100%;
    width: 0;
    display: inline-block;
    vertical-align: middle;
}
.iu-input__validateIcon {
    pointer-events: none;
}
.iu-input.is-disabled .iu-input__inner {
    background-color: #f5f7fa;
    border-color: #e4e7ed;
    color: #c0c4cc;
    cursor: not-allowed;
}
.iu-input.is-disabled .iu-input__inner::-webkit-input-placeholder {
    color: #c0c4cc;
}
.iu-input.is-disabled .iu-input__inner:-ms-input-placeholder {
    color: #c0c4cc;
}
.iu-input.is-disabled .iu-input__inner::-ms-input-placeholder {
    color: #c0c4cc;
}
.iu-input.is-disabled .iu-input__inner::placeholder {
    color: #c0c4cc;
}
.iu-input.is-disabled .iu-input__icon {
    cursor: not-allowed;
}
.iu-input.is-exceed .iu-input__inner {
    border-color: #f56c6c;
}
.iu-input.is-exceed .iu-input__suffix .iu-input__count {
    color: #f56c6c;
}
.iu-input--suffix .iu-input__inner {
    padding-right: 30px;
}
.iu-input--prefix .iu-input__inner {
    padding-left: 30px;
}
.iu-input--medium {
    font-size: 14px;
}
.iu-input--medium .iu-input__inner {
    height: 36px;
    line-height: 36px;
}
.iu-input--medium .iu-input__icon {
    line-height: 36px;
}
.iu-input--small {
    font-size: 13px;
}
.iu-input--small .iu-input__inner {
    height: 32px;
    line-height: 32px;
}
.iu-input--small .iu-input__icon {
    line-height: 32px;
}
.iu-input--mini {
    font-size: 12px;
}
.iu-input--mini .iu-input__inner {
    height: 28px;
    line-height: 28px;
}
.iu-input--mini .iu-input__icon {
    line-height: 28px;
}
.iu-input-group {
    line-height: normal;
    display: inline-table;
    width: 100%;
    border-collapse: separate;
    border-spacing: 0;
}
.iu-input-group > .iu-input__inner {
    vertical-align: middle;
    display: table-cell;
}
.iu-input-group__append,
.iu-input-group__prepend {
    background-color: #f5f7fa;
    color: #909399;
    vertical-align: middle;
    display: table-cell;
    position: relative;
    border: 1px solid #dcdfe6;
    border-radius: 4px;
    padding: 0 20px;
    width: 1px;
    white-space: nowrap;
}
.iu-input-group--prepend .iu-input__inner,
.iu-input-group__append {
    border-top-left-radius: 0;
    border-bottom-left-radius: 0;
}
.iu-input-group--append .iu-input__inner,
.iu-input-group__prepend {
    border-top-right-radius: 0;
    border-bottom-right-radius: 0;
}
.iu-input-group__append:focus,
.iu-input-group__prepend:focus {
    outline: 0;
}
.iu-input-group__append .iu-button,
.iu-input-group__append .iu-select,
.iu-input-group__prepend .iu-button,
.iu-input-group__prepend .iu-select {
    display: inline-block;
    margin: -10px -20px;
}
.iu-input-group__append button.iu-button,
.iu-input-group__append div.iu-select .iu-input__inner,
.iu-input-group__append div.iu-select:hover .iu-input__inner,
.iu-input-group__prepend button.iu-button,
.iu-input-group__prepend div.iu-select .iu-input__inner,
.iu-input-group__prepend div.iu-select:hover .iu-input__inner {
    border-color: transparent;
    background-color: transparent;
    color: inherit;
    border-top: 0;
    border-bottom: 0;
}
.iu-input-group__append .iu-button,
.iu-input-group__append .iu-input,
.iu-input-group__prepend .iu-button,
.iu-input-group__prepend .iu-input {
    font-size: inherit;
}
.iu-input-group__prepend {
    border-right: 0;
}
.iu-input-group__append {
    border-left: 0;
}
.iu-input-group--append .iu-select .iu-input.is-focus .iu-input__inner,
.iu-input-group--prepend .iu-select .iu-input.is-focus .iu-input__inner {
    border-color: transparent;
}
.iu-input__inner::-ms-clear {
    display: none;
    width: 0;
    height: 0;
}
.iu-scrollbar {
    overflow: hidden;
    position: relative;
}
.iu-scrollbar:active > .iu-scrollbar__bar,
.iu-scrollbar:focus > .iu-scrollbar__bar,
.iu-scrollbar:hover > .iu-scrollbar__bar {
    opacity: 1;
    -webkit-transition: opacity 340ms ease-out;
    transition: opacity 340ms ease-out;
}
.iu-scrollbar__wrap {
    overflow: scroll;
    height: 100%;
}
.iu-scrollbar__wrap--hidden-default {
    scrollbar-width: none;
}
.iu-scrollbar__wrap--hidden-default::-webkit-scrollbar {
    width: 0;
    height: 0;
}
.iu-scrollbar__thumb {
    position: relative;
    display: block;
    width: 0;
    height: 0;
    cursor: pointer;
    border-radius: inherit;
    background-color: rgba(144, 147, 153, 0.3);
    -webkit-transition: 0.3s background-color;
    transition: 0.3s background-color;
}
.iu-scrollbar__thumb:hover {
    background-color: rgba(144, 147, 153, 0.5);
}
.iu-scrollbar__bar {
    position: absolute;
    right: 2px;
    bottom: 2px;
    z-index: 1;
    border-radius: 4px;
    opacity: 0;
    -webkit-transition: opacity 120ms ease-out;
    transition: opacity 120ms ease-out;
}
.iu-scrollbar__bar.is-vertical {
    width: 6px;
    top: 2px;
}
.iu-scrollbar__bar.is-vertical > div {
    width: 100%;
}
.iu-scrollbar__bar.is-horizontal {
    height: 6px;
    left: 2px;
}
.iu-scrollbar__bar.is-horizontal > div {
    height: 100%;
}
.iu-popper .popper__arrow,
.iu-popper .popper__arrow::after {
    position: absolute;
    display: block;
    width: 0;
    height: 0;
    border-color: transparent;
    border-style: solid;
}
.iu-popper .popper__arrow {
    border-width: 6px;
    -webkit-filter: drop-shadow(0 2px 12px rgba(0, 0, 0, 0.03));
    filter: drop-shadow(0 2px 12px rgba(0, 0, 0, 0.03));
}
.iu-popper .popper__arrow::after {
    content: ' ';
    border-width: 6px;
}
.iu-popper[x-placement^='top'] {
    margin-bottom: 12px;
}
.iu-popper[x-placement^='top'] .popper__arrow {
    bottom: -6px;
    left: 50%;
    margin-right: 3px;
    border-top-color: #d1d5de;
    border-bottom-width: 0;
}
.iu-popper[x-placement^='top'] .popper__arrow::after {
    bottom: 1px;
    margin-left: -6px;
    border-top-color: #fff;
    border-bottom-width: 0;
}
.iu-popper[x-placement^='bottom'] {
    margin-top: 12px;
}
.iu-popper[x-placement^='bottom'] .popper__arrow {
    top: -6px;
    left: 50%;
    margin-right: 3px;
    border-top-width: 0;
    border-bottom-color: #d1d5de;
}
.iu-popper[x-placement^='bottom'] .popper__arrow::after {
    top: 1px;
    margin-left: -6px;
    border-top-width: 0;
    border-bottom-color: #fff;
}
.iu-popper[x-placement^='right'] {
    margin-left: 12px;
}
.iu-popper[x-placement^='right'] .popper__arrow {
    top: 50%;
    left: -6px;
    margin-bottom: 3px;
    border-right-color: #d1d5de;
    border-left-width: 0;
}
.iu-popper[x-placement^='right'] .popper__arrow::after {
    bottom: -6px;
    left: 1px;
    border-right-color: #fff;
    border-left-width: 0;
}
.iu-popper[x-placement^='left'] {
    margin-right: 12px;
}
.iu-popper[x-placement^='left'] .popper__arrow {
    top: 50%;
    right: -6px;
    margin-bottom: 3px;
    border-right-width: 0;
    border-left-color: #d1d5de;
}
.iu-popper[x-placement^='left'] .popper__arrow::after {
    right: 1px;
    bottom: -6px;
    margin-left: -6px;
    border-right-width: 0;
    border-left-color: #fff;
}
</style>
